import { useState, type FormEvent } from "react";

const FORM_ID = "dajs0frbcsf0ijchctig";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dajs0frbcsf0ijchctig";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们已收到并会尽快查看。");
      form.reset();
      return;
    }

    const content = formData.get("message");
    if (typeof content === "string" && content.length > 500) {
      setStatus("error");
      setFeedback("留言内容请控制在 500 字以内。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "website_alt") payload.append(key, String(value));
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，感谢你的反馈。我们会尽快查看，并在需要时与你联系。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          serverMsg || "提交失败，请检查内容后重试，或直接发送邮件至 hello@51baoliao.com。",
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试或通过邮箱联系我们。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select id="contact-topic" name="topic" className={inputClass} defaultValue="提供线索">
          <option value="提供线索">提供线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="转载与授权">转载与授权</option>
          <option value="合作建议">合作建议</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={500}
          required
          placeholder="请尽量写清时间、地点与关键事实，以及可以核实的方式（500 字以内）"
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1 text-xs text-foreground-500">最多 500 字，请勿包含个人隐私信息。</p>
      </div>

      <div className="field-alt-note" aria-hidden="true">
        <label htmlFor="contact-website-alt">请勿填写此栏</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" />
              提交反馈
            </>
          )}
        </button>
        <p className="text-xs text-foreground-500">提交即表示你同意我们仅将信息用于内容核实与回复。</p>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line mt-0.5" />
          {feedback}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {feedback}
        </p>
      )}
    </form>
  );
}